/**
 * Store module for tracking pending featured media uploads
 * Persists to localStorage to survive page refreshes
 */

const STORAGE_KEY = 'pendingFeaturedMedia';
const STALE_THRESHOLD = 24 * 60 * 60 * 1000; // 24 hours

// Load initial state from localStorage
function loadFromStorage() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return {};
    
    const parsed = JSON.parse(stored);
    const now = Date.now();
    
    // Clean up stale entries (older than 24 hours)
    const cleaned = Object.entries(parsed).reduce((acc, [documentHash, data]) => {
      if (now - data.timestamp < STALE_THRESHOLD) {
        acc[documentHash] = data;
      }
      return acc;
    }, {});
    
    return cleaned;
  } catch (error) {
    console.error('Failed to load pending featured media from storage:', error);
    return {};
  }
}

// Save to localStorage
function saveToStorage(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state.pending));
  } catch (error) {
    console.error('Failed to save pending featured media to storage:', error);
  }
}

const namespaced = true;

const state = {
  pending: loadFromStorage()
};

const mutations = {
  ADD(state, { documentHash, uploadId }) {
    state.pending = {
      ...state.pending,
      [documentHash]: {
        uploadId,
        timestamp: Date.now(),
        status: 'uploading'
      }
    };
    saveToStorage(state);
  },
  
  UPDATE_STATUS(state, { documentHash, status }) {
    if (state.pending[documentHash]) {
      state.pending[documentHash].status = status;
      saveToStorage(state);
    }
  },
  
  SET_ERROR(state, { documentHash, error }) {
    if (state.pending[documentHash]) {
      state.pending[documentHash].status = 'error';
      state.pending[documentHash].error = error;
      saveToStorage(state);
    }
  },
  
  REMOVE(state, documentHash) {
    const { [documentHash]: removed, ...rest } = state.pending;
    state.pending = rest;
    saveToStorage(state);
  },
  
  CLEAR_ALL(state) {
    state.pending = {};
    localStorage.removeItem(STORAGE_KEY);
  }
};

const getters = {
  GET_BY_DOCUMENT: (state) => (documentHash) => {
    return state.pending[documentHash] || null;
  },
  
  GET_BY_UPLOAD_ID: (state) => (uploadId) => {
    const entry = Object.entries(state.pending).find(
      ([hash, data]) => data.uploadId === uploadId
    );
    return entry ? { documentHash: entry[0], ...entry[1] } : null;
  },
  
  HAS_PENDING: (state) => (documentHash) => {
    return !!state.pending[documentHash];
  },
  
  ALL_PENDING: (state) => {
    return state.pending;
  },
  
  PENDING_COUNT: (state) => {
    return Object.keys(state.pending).length;
  }
};

export default {
  namespaced,
  state,
  mutations,
  getters
};
